import React from 'react';
import { AssemblyLayout } from 'src/components/assemblyLayout';
import { AssemblyScreen } from 'src/components/assemblyScreen';
import { AssemblyScreenSection } from 'src/components/assemblyScreenSection';
import { GCFComponentLayout } from 'src/components/gcfComponentLayout';
import { FullGcfComponentSection } from 'src/components/gcfComponentSection';
import { generateGridLayout } from 'src/components/generateGridLayout/GenerateGridLayout';
import { CodJourney } from 'src/constants/assemblyJourneys';
import { ComponentTypes, IResponseType, SectionTypes } from 'src/interfaces';
import { getResponsiveScale } from 'src/utils';
import { getSectionPreviewData } from 'src/utils/gcf-helper';

import {
  AssemblyLayoutWrapper,
  StyledProductContainer,
  StyledProductImage,
} from '../../PreviewModal.styled';
import { PreviewModalContentProps } from './PreviewModalContent.interface';

export const PreviewModalContent = React.memo(
  (props: PreviewModalContentProps) => {
    const [activeAssemblyJourney, setActiveAssemblyJourney] =
      React.useState<CodJourney>(CodJourney.CUSTOMER_ENTERS_DRIVE_THRU);

    React.useEffect(() => {
      const interval = setInterval(() => {
        setActiveAssemblyJourney((prevJourneyState) => {
          const currentIndex =
            Object.values(CodJourney)?.indexOf(prevJourneyState);

          return Object.values(CodJourney)[
            (currentIndex + 1) % Object.values(CodJourney)?.length
          ];
        });
      }, 3000);

      return () => clearInterval(interval); // Clean up the interval on component unmount
    }, []);

    switch (props.type) {
      case 'component': {
        return (
          <FullGcfComponentSection
            assemblyComponents={[
              {
                component: {
                  type: props.content.component_type?.name,
                  config: props.content?.config,
                  name: props.content.name,
                  width: props.content.width,
                  height: props.content.height,
                  theme: props.content.theme?.name,
                },
              },
            ]}
            height={props?.content?.height || 0}
            width={props?.content?.width || 0}
            containerSize={props.containerSize}
            previewData={getSectionPreviewData(
              props.content.component_type?.name as ComponentTypes,
            )}
            overrideQuerySetting={{
              staleTime: Infinity,
            }}
            productFilters={props.productFilters}
            productVariationFilters={props.productVariationFilters}
            skipProductsApiOnEmptyFilters={props.skipProductsApiOnEmptyFilters}
          />
        );
      }
      case 'assembly': {
        return (
          <GCFComponentLayout
            fullSize
            containerSize={props.containerSize}
            layout={props.layout}
            assemblyComponents={props.assemblyComponents}
            productFilters={props.productFilters}
            productVariationFilters={props.productVariationFilters}
            skipProductsApiOnEmptyFilters={props.skipProductsApiOnEmptyFilters}
          />
        );
      }

      case 'product': {
        return (
          <StyledProductContainer>
            <StyledProductImage
              src={props.content?.previewUrl}
              alt="preview asset"
              height={props.content?.height}
              width={props.content?.width}
            />
          </StyledProductContainer>
        );
      }

      case 'layout': {
        const scale = getResponsiveScale(
          props.containerSize?.size?.width ?? 0,
          props.content.screens,
          IResponseType.MODAL,
        );

        return (
          <AssemblyLayoutWrapper>
            <AssemblyLayout>
              {props.content.screens.map((screen, index) => (
                <AssemblyScreen
                  key={screen.name + index}
                  height={screen.height}
                  width={screen.width}
                  scale={scale}
                  selectable={false}
                  type="screen">
                  {screen.name.includes('placeholder')
                    ? generateGridLayout(
                        props.content?.config?.gridLines?.[index] ?? '',
                        scale,
                      )
                    : screen.sections.map((section, index) => (
                        <AssemblyScreenSection
                          key={section.name + index}
                          section={section}
                          sectionTypes={props.content.config?.sectionTypes}
                          activeAssemblyJourney={activeAssemblyJourney}
                          scale={scale}
                          isCodConfirmationOpen={
                            !!props.content.config?.sectionTypes?.some(
                              (event: string) =>
                                event === SectionTypes.CONFIRMATION,
                            ) &&
                            activeAssemblyJourney ===
                              CodJourney.PURCHASE_IS_CONFIRMED
                          }
                          selectable={false}
                          showEditIcon={false}
                          type="grid"
                        />
                      ))}
                </AssemblyScreen>
              ))}
            </AssemblyLayout>
          </AssemblyLayoutWrapper>
        );
      }

      default:
        return null;
    }
  },
);
